import { EndpointError } from '@iconik-io/iconik-api/src/client/response';
import {
  type AssetsShareLoginSchema,
  type AssetsShareTokenSchema,
  httpGetByObjectTypeByObjectIdSharesByShareId,
  httpGetCollectionsByCollectionId,
} from '@iconik-io/iconik-api/src/services/assets';
import { setUserRoles } from '@iconik-io/shared-lib/src/redux/auth/slice';
import { rolesArrayToDictionary } from '@iconik-io/shared-lib/src/redux/auth/utils';
import {
  makeShareLoginThunk,
  type ShareLoginResult,
} from '@iconik-io/shared-lib/src/redux/share/auth_thunks';
import {
  handleErrors,
  type ShareEmbargo,
} from '@iconik-io/shared-lib/src/utils/handle_messages';
import { isRole } from '@iconik-io/shared-lib/src/utils/roles';

import { ID_COLLECTION } from '../../constants/reducer_ids';
import type { AppDispatch, GetState } from '../../store/types';
import { UUID_ZERO } from '../../utils/tree_handlers';
import { setSessionAjaxConfig } from '../app/slice';
import { setCurrentUser } from '../auth/slice';
import type { AuthUserData } from '../auth/types';
import { getCollectionContents } from '../collection_fetch/thunks';
import { historyPush } from '../history/actions';
import { errorMessage } from '../messages/thunks';
import { setAllRoles } from '../roles/slice';
import { getMergedSettings } from '../settings/thunks';
import { addBranches, clearTree, modifyBranchByID } from '../tree/thunks';
import { shareAuthLogin, shareAuthLoginLoading, shareGet } from './slice';

function getEntityShare(objectType: string, objectId: string, shareId: string) {
  return (dispatch: AppDispatch) =>
    dispatch(
      httpGetByObjectTypeByObjectIdSharesByShareId(
        objectType,
        objectId,
        shareId,
      ),
    ).then((response) => response.body);
}

/**
 * Installs a share token into the app: roles, current user, request headers and the
 * share entity. Shared by the hash login and the magic-link flow, which receives a bare
 * token rather than an endpoint response.
 */
export function applyShareToken(shareToken: AssetsShareTokenSchema) {
  return (dispatch: AppDispatch) => {
    const { object_type, object_id, share_id, email, token, app_id } =
      shareToken;
    const roles = (shareToken.roles ?? []).filter(isRole);

    dispatch(
      setAllRoles({
        objects: roles,
        standard: [],
        browse_only: [],
        browse_api_only: [],
      }),
    );
    dispatch(setUserRoles(rolesArrayToDictionary(roles)));

    dispatch(
      // TODO this is a hack to get the share email and share auth token schema into the user data
      // biome-ignore lint/plugin: see the TODO above.
      setCurrentUser({
        ...shareToken,
        shareEmail: email,
      } as unknown as AuthUserData),
    );

    const requestHeaders = { 'Share-Auth-Token': token, 'App-ID': app_id };
    dispatch(setSessionAjaxConfig({ requestHeaders, canRefresh: false }));
    dispatch(shareAuthLogin(shareToken));

    const mergedSettingsRequest = dispatch(getMergedSettings());
    if (!object_type || !object_id || !share_id) {
      return mergedSettingsRequest;
    }

    // Committing the share is what unblocks the page, so it waits on the
    // settings that decide DRM, watermarking and downloads. Both requests are
    // dispatched before the join, so they still leave in the same tick.
    return Promise.all([
      dispatch(getEntityShare(object_type, object_id, share_id)),
      mergedSettingsRequest,
    ])
      .then(([share, settings]) => {
        // Those settings fall back to defaults that read as "protection off",
        // so a share whose domain settings could not be read is withheld rather
        // than shown unprotected.
        if (!settings) {
          dispatch(historyPush('/invalid_share'));
          return;
        }
        dispatch(shareGet(share));
      })
      .catch(() => {
        dispatch(historyPush('/invalid_share'));
      });
  };
}

const baseShareLogin = makeShareLoginThunk(applyShareToken);

export const shareLogin =
  (data: AssetsShareLoginSchema) =>
  (dispatch: AppDispatch): Promise<ShareLoginResult> => {
    dispatch(shareAuthLoginLoading(true));
    return dispatch(baseShareLogin(data)).finally(() => {
      dispatch(shareAuthLoginLoading(false));
    });
  };

export type ShareLoginPasswordResult = {
  /** A field-level error, shown inline on the password field rather than as a toast. */
  passwordInvalid?: boolean;
  magicLinkRequired?: boolean;
  embargo?: ShareEmbargo;
};

/**
 * Submits the password prompt through the same thunk as the first, password-less
 * attempt. A share can need both a password and an identified viewer, so the
 * magic-link fallback has to stay reachable from here too.
 */
export function shareLoginPassword(data: AssetsShareLoginSchema) {
  return (dispatch: AppDispatch): Promise<ShareLoginPasswordResult> =>
    dispatch(shareLogin(data))
      .then(({ passwordRequired, magicLinkRequired, embargo }) => ({
        passwordInvalid: passwordRequired,
        magicLinkRequired,
        embargo,
      }))
      .catch((error: unknown) => {
        const errors =
          error instanceof EndpointError ? handleErrors(error) : [];
        dispatch(
          errorMessage({
            text: errors.length
              ? errors
              : ['Could not sign in. Please try again.'],
          }),
        );
        throw error;
      });
}

export function getShareSubcollections(id: string) {
  return async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
    if (!getState().collectionsTree.flatTree.length) {
      dispatch(clearTree(ID_COLLECTION));
      dispatch(modifyBranchByID(ID_COLLECTION, UUID_ZERO, { loading: true }));

      const topCollection = await dispatch(
        httpGetCollectionsByCollectionId(id),
      );

      dispatch(
        addBranches(ID_COLLECTION, {
          objects: [topCollection.body],
          index: 0,
        }),
      );

      dispatch(
        modifyBranchByID(ID_COLLECTION, topCollection.body.id, {
          expanded: true,
          loading: true,
        }),
      );

      // this promise appends the children branches
      // it requires the parent branch to be loaded and added to the tree
      return dispatch(
        getCollectionContents(id, {
          per_page: 1000,
          sort: 'title,asc',
          object_types: 'collections',
        }),
      ).finally(() => {
        dispatch(
          modifyBranchByID(ID_COLLECTION, topCollection.body.id, {
            loading: false,
          }),
        );
      });
    }
    return Promise.resolve();
  };
}
